import { Injectable, OnDestroy } from '@angular/core';
import { Socket as PhoenixSocket, Channel } from 'phoenix';
import { io, Socket as SocketIOSocket } from 'socket.io-client';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { LocalStorageService } from '../core/services/local-storage.service';
import { LocalStorageKeys } from '../core/constants/local-storage-keys.enum';

export interface ImportStep {
  id: string;
  displayName: string;
  status: 'pending' | 'waiting' | 'in-progress' | 'completed' | 'error' | 'skipped';
  progress: number;
  totalItems?: number;
  processedItems?: number;
  message?: string;
  order: number;
  startedAt?: number;
  completedAt?: number;
}

export interface OverallProgress {
  status: 'preparing' | 'in-progress' | 'completed' | 'failed';
  progress: number;
  currentStep?: string;
  totalSteps: number;
  completedSteps: number;
  startedAt: number;
  estimatedCompletionTime?: number;
  message?: string;
}

export interface ImportProgressData {
  merchantId: string;
  shopId: string;
  startedAt: number;
  stages: ImportStep[];
  overall: OverallProgress;
}

export interface ImportProgressEvent {
  type:
    | 'import.started'
    | 'import.progress'
    | 'import.completed'
    | 'import.core.completed'
    | 'import.failed'
    | 'step.started'
    | 'step.progress'
    | 'step.completed'
    | 'step.failed';
  merchantId: string;
  shopId: string;
  timestamp: number;
  data: {
    step?: ImportStep;
    stages?: ImportStep[];
    overall?: OverallProgress;
    error?: string;
    details?: any;
  };
}

export interface ActiveImportsData {
  merchantId: string;
  activeShops: string[];
  hasActiveImports: boolean;
  timestamp: number;
}

export interface ImportStatusChangedEvent {
  type: 'import.started' | 'import.completed' | 'import.failed';
  merchantId: string;
  shopId: string;
  timestamp: number;
  error?: string;
  showProgressModal?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ImportProgressService implements OnDestroy {
  // Elixir is the only import backend
  private useElixir = true;

  // Phoenix WebSocket (for Elixir backend)
  private phoenixSocket: PhoenixSocket | null = null;
  private globalChannel: Channel | null = null;
  private shopChannels = new Map<string, Channel>();

  // Socket.IO (for Node.js backend)
  private socketIOSocket: SocketIOSocket | null = null;

  // Shared state
  private connected$ = new BehaviorSubject<boolean>(false);
  private activeImports$ = new BehaviorSubject<ActiveImportsData | null>(null);
  private importProgress$ = new Subject<ImportProgressEvent>();
  private importStatusChanged$ = new Subject<ImportStatusChangedEvent>();
  private subscriptions = new Map<string, boolean>();
  private progressStateCache = new Map<string, ImportProgressEvent>();
  private merchantId: string | null = null;
  private lastHeartbeatTime = new Map<string, number>();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  constructor(
    private _httpClient: HttpClient,
    private localStorage: LocalStorageService
  ) {
    // Check if we're in a browser environment
    if (typeof window !== 'undefined') {
      // CRITICAL FIX: Only auto-connect if NOT using Elixir
      // If useElixir is true, ElixirImportProgressService will handle connection
      if (!this.useElixir) {
        this.connect();
      }
    }
  }

  ngOnDestroy() {
    this.disconnect();
  }

  /**
   * Connect to appropriate backend based on feature flag
   */
  connect(): void {
    if (this.useElixir) {
      this.connectToPhoenix();
    } else {
      this.connectToSocketIO();
    }
  }

  /**
   * Connect to Phoenix WebSocket server (Elixir backend)
   */
  private connectToPhoenix(): void {
    if (this.phoenixSocket?.isConnected()) {
      return;
    }

    const token = this.localStorage.get<string>(LocalStorageKeys.ACCESS_TOKEN);
    if (!token) {
      return;
    }

    // Determine the WebSocket URL based on environment
    // Convert HTTP URL to WebSocket URL
    // For example: http://localhost:4001 -> ws://localhost:4001/socket
    const wsUrl =
      environment.elixirApiUrl.replace(/^http/, 'ws').replace(/^https/, 'wss') + '/socket';

    // Create Phoenix Socket with JWT token
    this.phoenixSocket = new PhoenixSocket(wsUrl, {
      params: { token: token },
      reconnectAfterMs: (tries: number) => {
        // Exponential backoff: 1s, 2s, 4s, 8s, 16s
        return [1000, 2000, 4000, 8000, 16000][tries - 1] || 16000;
      },
      logger: (kind: string, msg: string, data: any) => {
        // Phoenix logger for debugging
      }
    });

    // Setup socket-level event listeners
    this.phoenixSocket.onOpen(() => {
      this.connected$.next(true);
      this.reconnectAttempts = 0;

      // Join the global channel on connection
      this.joinGlobalChannel();
    });

    this.phoenixSocket.onError((error: any) => {
      console.error('[Phoenix] Socket error:', error);
      this.connected$.next(false);
    });

    this.phoenixSocket.onClose(() => {
      this.connected$.next(false);
      this.subscriptions.clear();
      this.merchantId = null;
    });

    // Connect to the server
    this.phoenixSocket.connect();
  }

  /**
   * Connect to Socket.IO server (Node.js backend)
   */
  private connectToSocketIO(): void {
    if (this.socketIOSocket?.connected) {
      return;
    }

    const token = this.localStorage.get<string>(LocalStorageKeys.ACCESS_TOKEN);
    if (!token) {
      console.warn('[Socket.IO] ⚠️ No access token found, cannot connect');
      return;
    }

    // Determine the WebSocket URL
    // Use regex to replace only at the end to avoid removing 'api' from domain name like 'api.dashboardly.io'
    const wsUrl = environment.apiUrl.replace(/\/api$/, '');

    // Create Socket.IO connection
    this.socketIOSocket = io(`${wsUrl}/import-progress`, {
      transports: ['websocket'],
      auth: { token },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000
    });

    // Setup Socket.IO event listeners
    this.setupSocketIOListeners();
  }

  /**
   * Setup Socket.IO event listeners (Node.js backend)
   */
  private setupSocketIOListeners(): void {
    if (!this.socketIOSocket) {
      return;
    }

    // Connection events
    this.socketIOSocket.on('connect', () => {
      this.connected$.next(true);
      this.reconnectAttempts = 0;
    });

    this.socketIOSocket.on(
      'connection-success',
      (payload: { userId: string; merchantId: string; timestamp: number }) => {
        this.merchantId = payload.merchantId;

        // Automatically join merchant room
        // (already done on server-side, but we track it here)
      }
    );

    this.socketIOSocket.on('connection-error', (err: { code: string; message: string }) => {
      console.error('[Socket.IO] Connection error:', err);
      this.connected$.next(false);
    });

    this.socketIOSocket.on('disconnect', () => {
      this.connected$.next(false);
      this.subscriptions.clear();
    });

    this.socketIOSocket.on('connect_error', (error: any) => {
      console.error('[Socket.IO] Connect error:', error);
      this.connected$.next(false);
    });

    // Import progress events
    this.socketIOSocket.on('import-progress', (event: ImportProgressEvent) => {
      if (event.shopId) {
        this.progressStateCache.set(event.shopId, event);
        this.lastHeartbeatTime.set(event.shopId, Date.now());
      }

      this.importProgress$.next(event);
    });

    // Active imports updates
    this.socketIOSocket.on('active-imports', (payload: ActiveImportsData) => {
      this.activeImports$.next(payload);
    });

    // Import status changes
    this.socketIOSocket.on('import-status-changed', (event: ImportStatusChangedEvent) => {
      this.importStatusChanged$.next(event);

      // Update active imports on status change
      if (this.merchantId) {
        // Request will be sent automatically by backend
      }
    });

    // Heartbeat event
    this.socketIOSocket.on('import-heartbeat', (payload: { shopId: string; timestamp: number }) => {
      this.lastHeartbeatTime.set(payload.shopId, Date.now());

      // Emit a progress event to update activity tracking
      this.importProgress$.next({
        type: 'import.progress',
        merchantId: this.merchantId || '',
        shopId: payload.shopId,
        timestamp: payload.timestamp,
        data: {}
      });
    });

    // Subscription confirmation
    this.socketIOSocket.on(
      'subscribed',
      (payload: { merchantId: string; shopId: string; room: string }) => {
        this.subscriptions.set(payload.shopId, true);
      }
    );
  }

  /**
   * Join the global import channel for merchant-level events (Phoenix only)
   */
  private joinGlobalChannel(): void {
    if (!this.phoenixSocket) {
      return;
    }

    // Join the global channel
    this.globalChannel = this.phoenixSocket.channel('import:global', {});

    this.globalChannel
      .join()
      .receive('ok', (resp: any) => {
        // Extract merchant ID from response if available
        if (resp.merchantId) {
          this.merchantId = resp.merchantId;
        }
      })
      .receive('error', (resp: any) => {
        console.error('[Phoenix] Failed to join global channel:', resp);
      })
      .receive('timeout', () => {
        console.error('[Phoenix] Global channel join timeout');
      });

    // Setup event listeners for the global channel
    this.setupGlobalChannelListeners();
  }

  /**
   * Setup event listeners for the global channel
   */
  private setupGlobalChannelListeners(): void {
    if (!this.globalChannel) {
      return;
    }

    // Progress update event (sent from Phoenix Channel)
    this.globalChannel.on('progress_update', (payload: any) => {
      // Convert Phoenix payload to ImportProgressEvent format
      const event: ImportProgressEvent = {
        type: payload.type || 'import.progress',
        merchantId: payload.merchantId || this.merchantId || '',
        shopId: payload.shopId,
        timestamp: payload.timestamp || Date.now(),
        data: {
          step: payload.step,
          stages: payload.stages,
          overall: payload.overall,
          error: payload.error,
          details: payload.details
        }
      };

      if (event.shopId) {
        this.progressStateCache.set(event.shopId, event);
        this.lastHeartbeatTime.set(event.shopId, Date.now());
      }

      this.importProgress$.next(event);
    });

    // Import started event - listen with DOT notation (Phoenix sends "import.started")
    this.globalChannel.on('import.started', (payload: any) => {
      const event: ImportStatusChangedEvent = {
        type: 'import.started',
        merchantId: payload.merchantId || this.merchantId || '',
        shopId: payload.shopId,
        timestamp: payload.timestamp || Date.now(),
        showProgressModal: payload.showProgressModal
      };

      this.importStatusChanged$.next(event);

      // Update active imports
      this.updateActiveImports();
    });

    // LEGACY: Also listen for underscore version for backward compatibility
    this.globalChannel.on('import_started', (payload: any) => {
      const event: ImportStatusChangedEvent = {
        type: 'import.started',
        merchantId: payload.merchantId || this.merchantId || '',
        shopId: payload.shopId,
        timestamp: payload.timestamp || Date.now(),
        showProgressModal: payload.showProgressModal
      };

      this.importStatusChanged$.next(event);

      // Update active imports
      this.updateActiveImports();
    });

    // Import completed event
    this.globalChannel.on('import_completed', (payload: any) => {
      const event: ImportStatusChangedEvent = {
        type: 'import.completed',
        merchantId: payload.merchantId || this.merchantId || '',
        shopId: payload.shopId,
        timestamp: payload.timestamp || Date.now()
      };

      this.importStatusChanged$.next(event);

      // Update active imports
      this.updateActiveImports();
    });

    // Import failed event
    this.globalChannel.on('import_failed', (payload: any) => {
      const event: ImportStatusChangedEvent = {
        type: 'import.failed',
        merchantId: payload.merchantId || this.merchantId || '',
        shopId: payload.shopId,
        timestamp: payload.timestamp || Date.now(),
        error: payload.error
      };

      this.importStatusChanged$.next(event);

      // Update active imports
      this.updateActiveImports();
    });

    // Active imports broadcast
    this.globalChannel.on('active_imports', (payload: ActiveImportsData) => {
      this.activeImports$.next(payload);
    });

    // Heartbeat event
    this.globalChannel.on('import_heartbeat', (payload: { shopId: string; timestamp: number }) => {
      this.lastHeartbeatTime.set(payload.shopId, Date.now());

      // Emit a progress event to update activity tracking
      this.importProgress$.next({
        type: 'import.progress',
        merchantId: this.merchantId || '',
        shopId: payload.shopId,
        timestamp: payload.timestamp,
        data: {}
      });
    });
  }

  /**
   * Update active imports list from server
   */
  private updateActiveImports(): void {
    if (!this.globalChannel) {
      return;
    }

    this.globalChannel
      .push('get_active_imports', {})
      .receive('ok', (resp: any) => {
        if (resp.activeImports) {
          this.activeImports$.next(resp.activeImports);
        }
      })
      .receive('error', (resp: any) => {
        console.error('[Phoenix] Failed to get active imports:', resp);
      });
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    // Phoenix cleanup
    if (this.phoenixSocket) {
      // Leave all shop channels
      this.shopChannels.forEach((channel, shopId) => {
        channel.leave();
      });
      this.shopChannels.clear();

      // Leave global channel
      if (this.globalChannel) {
        this.globalChannel.leave();
        this.globalChannel = null;
      }

      // Disconnect Phoenix socket
      this.phoenixSocket.disconnect();
      this.phoenixSocket = null;
    }

    // Socket.IO cleanup
    if (this.socketIOSocket) {
      this.socketIOSocket.removeAllListeners();
      this.socketIOSocket.disconnect();
      this.socketIOSocket = null;
    }

    this.connected$.next(false);
  }

  /**
   * Subscribe to specific shop import progress
   */
  subscribeToShop(shopId: string): void {
    if (this.useElixir) {
      this.subscribeToShopPhoenix(shopId);
    } else {
      this.subscribeToShopSocketIO(shopId);
    }
  }

  /**
   * Subscribe to shop via Phoenix (Elixir backend)
   */
  private subscribeToShopPhoenix(shopId: string): void {
    if (!this.phoenixSocket?.isConnected()) {
      console.warn('[Phoenix] Cannot subscribe to shop - not connected');
      return;
    }

    // Check if already subscribed
    if (this.shopChannels.has(shopId)) {
      return;
    }

    // Create channel topic: import:progress:{shopId}
    // NOTE: merchantId is no longer needed in topic - Elixir looks it up from shop_id
    const channelTopic = `import:progress:${shopId}`;
    const channel = this.phoenixSocket.channel(channelTopic, {});

    channel
      .join()
      .receive('ok', (resp: any) => {
        this.subscriptions.set(shopId, true);

        // Request current progress state
        this.requestCurrentProgress(shopId, channel);
      })
      .receive('error', (resp: any) => {
        console.error('[Phoenix] Failed to subscribe to shop:', shopId, resp);
      })
      .receive('timeout', () => {
        console.error('[Phoenix] Shop channel join timeout:', shopId);
      });

    // Setup shop-specific event listeners
    channel.on('progress_update', (payload: any) => {
      const event: ImportProgressEvent = {
        type: payload.type || 'import.progress',
        merchantId: payload.merchantId || this.merchantId || '',
        shopId: shopId,
        timestamp: payload.timestamp || Date.now(),
        data: {
          step: payload.step,
          stages: payload.stages,
          overall: payload.overall,
          error: payload.error,
          details: payload.details
        }
      };

      this.progressStateCache.set(shopId, event);
      this.lastHeartbeatTime.set(shopId, Date.now());
      this.importProgress$.next(event);
    });

    this.shopChannels.set(shopId, channel);
  }

  /**
   * Subscribe to shop via Socket.IO (Node.js backend)
   */
  private subscribeToShopSocketIO(shopId: string): void {
    if (!this.socketIOSocket?.connected) {
      console.warn('[Socket.IO] ⚠️ Cannot subscribe to shop - not connected');
      return;
    }

    // Check if already subscribed
    if (this.subscriptions.has(shopId)) {
      return;
    }

    // Emit subscribe event to join shop room
    this.socketIOSocket.emit('subscribe', { shopId });

    // Mark as subscribed (confirmation will come via 'subscribed' event)
    this.subscriptions.set(shopId, true);
  }

  /**
   * Request current progress state for a shop
   */
  requestCurrentProgress(shopId: string, channel?: Channel): void {
    const shopChannel = channel || this.shopChannels.get(shopId);
    if (!shopChannel) {
      console.warn('[Phoenix] No channel found for shop:', shopId);
      return;
    }

    shopChannel
      .push('get_progress', { shopId })
      .receive('ok', (resp: any) => {
        if (resp.progress) {
          const event: ImportProgressEvent = {
            type: 'import.progress',
            merchantId: resp.progress.merchantId || this.merchantId || '',
            shopId: shopId,
            timestamp: Date.now(),
            data: {
              stages: resp.progress.stages,
              overall: resp.progress.overall
            }
          };
          this.progressStateCache.set(shopId, event);
          this.importProgress$.next(event);
        }
      })
      .receive('error', (resp: any) => {
        console.error('[Phoenix] Failed to get progress:', shopId, resp);
      });
  }

  /**
   * Unsubscribe from specific shop import progress
   */
  unsubscribeFromShop(shopId: string): void {
    if (this.useElixir) {
      // Phoenix unsubscribe
      const channel = this.shopChannels.get(shopId);
      if (channel) {
        channel.leave();
        this.shopChannels.delete(shopId);
      }
    } else {
      // Socket.IO unsubscribe
      if (this.socketIOSocket?.connected) {
        this.socketIOSocket.emit('unsubscribe', { shopId });
      }
    }

    this.subscriptions.delete(shopId);
    this.lastHeartbeatTime.delete(shopId);
  }

  /**
   * Subscribe to merchant-level events (handled by global channel)
   */
  subscribeToMerchant(): void {
    // Already handled by global channel
  }

  /**
   * Get connection status
   */
  getConnectionStatus(): Observable<boolean> {
    return this.connected$.asObservable();
  }

  /**
   * Check if currently connected
   */
  isConnected(): boolean {
    if (this.useElixir) {
      return this.phoenixSocket?.isConnected() || false;
    } else {
      return this.socketIOSocket?.connected || false;
    }
  }

  /**
   * Get diagnostic information about the connection
   */
  getDiagnostics(): any {
    if (this.useElixir) {
      if (!this.phoenixSocket) {
        return {
          backend: 'elixir',
          status: 'No socket instance',
          socket: null
        };
      }

      return {
        backend: 'elixir',
        status: 'Socket instance exists',
        connected: this.phoenixSocket.isConnected(),
        connectionState: this.phoenixSocket.connectionState(),
        channels: Array.from(this.shopChannels.keys()),
        globalChannel: this.globalChannel ? 'connected' : 'disconnected',
        merchantId: this.merchantId,
        subscriptions: Array.from(this.subscriptions.keys())
      };
    } else {
      if (!this.socketIOSocket) {
        return {
          backend: 'nodejs',
          status: 'No socket instance',
          socket: null
        };
      }

      return {
        backend: 'nodejs',
        status: 'Socket instance exists',
        connected: this.socketIOSocket.connected,
        merchantId: this.merchantId,
        subscriptions: Array.from(this.subscriptions.keys())
      };
    }
  }

  /**
   * Get active imports
   */
  getActiveImports(): Observable<ActiveImportsData | null> {
    return this.activeImports$.asObservable();
  }

  /**
   * Get import progress events
   */
  getImportProgress(): Observable<ImportProgressEvent> {
    return this.importProgress$.asObservable();
  }

  /**
   * Get import status changed events
   */
  getImportStatusChanged(): Observable<ImportStatusChangedEvent> {
    return this.importStatusChanged$.asObservable();
  }

  /**
   * Get current progress from HTTP API (fallback)
   */
  getCurrentProgress(shopId: string): Observable<ImportProgressData | null> {
    return this._httpClient.get<ImportProgressData | null>(
      `${environment.apiUrl}/imports/progress/${shopId}`
    );
  }

  /**
   * Get the last heartbeat time for a shop
   */
  getLastHeartbeatTime(shopId: string): number | undefined {
    return this.lastHeartbeatTime.get(shopId);
  }

  /**
   * Check if a shop import is considered active based on heartbeat
   */
  isImportActiveByHeartbeat(shopId: string, maxInactivityMs: number = 60000): boolean {
    const lastHeartbeat = this.lastHeartbeatTime.get(shopId);
    if (!lastHeartbeat) {
      return false;
    }
    return Date.now() - lastHeartbeat < maxInactivityMs;
  }

  /**
   * Get cached progress for a shop (if available)
   * This is useful when modal opens after events have already been received
   */
  getCachedProgress(shopId: string): ImportProgressEvent | undefined {
    return this.progressStateCache.get(shopId);
  }

  /**
   * DEBUG: Stop heartbeat for testing timeout functionality
   */
  debugStopHeartbeat(shopId: string): void {
    if (!this.globalChannel) {
      console.warn('[Phoenix] Cannot stop heartbeat - no global channel');
      return;
    }

    this.globalChannel
      .push('debug_stop_heartbeat', { shopId })
      .receive('ok', (resp: any) => {})
      .receive('error', (resp: any) => {
        console.error('[Phoenix] Failed to stop heartbeat:', shopId, resp);
      });
  }
}
